import { useState, useMemo } from "react";
import { format, addDays, addMonths, differenceInDays, isBefore, parseISO, startOfMonth } from "date-fns";
import { Plus, Car, FileText, Check, Wrench, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Vehicle } from "@/hooks/useVehicles";
import { MaintenanceTask } from "@/hooks/useMaintenance";
import { MaintenanceTaskDialog, MaintenanceTaskFormValues } from "./MaintenanceTaskDialog";
import { DeleteTaskDialog } from "./DeleteTaskDialog";

interface VehicleMaintenanceTimelineProps {
  vehicle: Vehicle;
  tasks: MaintenanceTask[];
  onAddTask: (vehicleId: string, values: MaintenanceTaskFormValues, attachments: string[]) => void;
  onEditTask: (taskId: string, values: MaintenanceTaskFormValues, attachments: string[]) => void;
  onDeleteTask: (taskId: string) => void;
  onToggleComplete: (id: string, isCompleted: boolean) => void;
  isAddingTask: boolean;
  isEditingTask: boolean;
  isDeletingTask: boolean;
  uploadAttachment: (file: File) => Promise<string>;
}

export const VehicleMaintenanceTimeline = ({
  vehicle,
  tasks,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onToggleComplete,
  isAddingTask,
  isEditingTask,
  isDeletingTask,
  uploadAttachment,
}: VehicleMaintenanceTimelineProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<MaintenanceTask | null>(null);

  // Calculate timeline range
  const timelineData = useMemo(() => {
    const today = new Date();
    const startDate = addDays(today, -30); // 30 days in the past
    const endDate = addDays(today, 180); // 180 days in the future
    const totalDays = differenceInDays(endDate, startDate);

    // Estimate due date from mileage if no due_date
    const estimateDueDate = (task: MaintenanceTask): Date | null => {
      if (task.due_date) {
        return parseISO(task.due_date);
      }
      if (task.due_at_mileage && vehicle.mileage) {
        // Estimate ~1000 miles per month
        const milesRemaining = task.due_at_mileage - vehicle.mileage;
        if (milesRemaining <= 0) return today;
        const daysRemaining = Math.ceil((milesRemaining / 1000) * 30);
        return addDays(today, daysRemaining);
      }
      return null;
    };

    const positionedTasks = tasks
      .map((task) => {
        const estimatedDate = estimateDueDate(task);
        if (!estimatedDate) return null;
        
        const daysFromStart = differenceInDays(estimatedDate, startDate);
        const position = Math.max(0, Math.min(100, (daysFromStart / totalDays) * 100));
        
        const isOverdue = isBefore(estimatedDate, today) && !task.is_completed;
        const isEstimated = !task.due_date && !!task.due_at_mileage;

        return {
          ...task,
          estimatedDate,
          position,
          isOverdue,
          isEstimated,
        };
      })
      .filter(Boolean) as Array<MaintenanceTask & { 
        estimatedDate: Date; 
        position: number; 
        isOverdue: boolean;
        isEstimated: boolean;
      }>;

    // Position of today on the timeline
    const todayPosition = (differenceInDays(today, startDate) / totalDays) * 100;

    // Generate monthly markers
    const monthMarkers: Array<{ date: Date; position: number; label: string }> = [];
    let currentMonth = startOfMonth(addMonths(startDate, 1));
    while (isBefore(currentMonth, endDate)) {
      const daysFromStart = differenceInDays(currentMonth, startDate);
      const position = (daysFromStart / totalDays) * 100;
      if (position > 5 && position < 95) {
        monthMarkers.push({
          date: currentMonth,
          position,
          label: format(currentMonth, "MMM d"),
        });
      }
      currentMonth = addMonths(currentMonth, 1);
    }

    return { startDate, endDate, positionedTasks, todayPosition, today, monthMarkers };
  }, [tasks, vehicle.mileage]);

  const handleAddSubmit = (values: MaintenanceTaskFormValues, attachments: string[]) => {
    onAddTask(vehicle.id, values, attachments);
    setIsAddDialogOpen(false);
  };

  const handleEditSubmit = (values: MaintenanceTaskFormValues, attachments: string[]) => {
    if (selectedTask) {
      onEditTask(selectedTask.id, values, attachments);
      setIsEditDialogOpen(false);
      setSelectedTask(null);
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedTask) {
      onDeleteTask(selectedTask.id);
      setIsDeleteDialogOpen(false);
      setSelectedTask(null);
    }
  };

  const openEditDialog = (task: MaintenanceTask) => {
    setSelectedTask(task);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (task: MaintenanceTask) => {
    setSelectedTask(task);
    setIsDeleteDialogOpen(true);
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-20 w-20">
              <AvatarImage src={vehicle.image_url || ""} alt={vehicle.name} />
              <AvatarFallback>
                <Car className="h-10 w-10" />
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-foreground">{vehicle.name}</h3>
              <p className="text-sm text-muted-foreground">
                {vehicle.year} {vehicle.make} {vehicle.model}
                {vehicle.mileage && ` • ${vehicle.mileage.toLocaleString()} mi`}
              </p>
            </div>
          </div>
          <Button size="sm" onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Task
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {tasks.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Wrench className="h-5 w-5 mr-2" />
            <span>No maintenance tasks scheduled</span>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline track */}
            <div className="relative h-24 bg-muted/50 rounded-lg overflow-hidden">
              {/* Today marker */}
              <div 
                className="absolute top-0 bottom-0 w-0.5 bg-primary z-10"
                style={{ left: `${timelineData.todayPosition}%` }}
              />

              {/* Task markers */}
              {timelineData.positionedTasks.map((task) => (
                <HoverCard key={task.id} openDelay={100} closeDelay={200}>
                  <HoverCardTrigger asChild>
                    <button
                      className={cn(
                        "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 transition-transform hover:scale-110",
                        "flex flex-col items-center cursor-pointer"
                      )}
                      style={{ left: `${task.position}%` }}
                    >
                      <div className={cn(
                        "flex items-center justify-center w-5 h-5 rounded-full shadow-sm",
                        task.is_completed 
                          ? "bg-green-700 text-white" 
                          : task.isOverdue 
                            ? "bg-destructive text-destructive-foreground"
                            : "bg-primary text-primary-foreground"
                      )}>
                        {task.is_completed && (
                          <Check className="h-3 w-3" strokeWidth={3} />
                        )}
                      </div>
                      <div className={cn(
                        "mt-1.5 text-[10px] font-medium max-w-[70px] text-center leading-tight",
                        task.is_completed 
                          ? "text-green-700" 
                          : task.isOverdue
                            ? "text-destructive"
                            : "text-foreground"
                      )}>
                        {task.task_name}
                      </div>
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-72" side="top">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <h4 className="font-semibold">{task.task_name}</h4>
                        {task.is_completed && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                            Completed
                          </span>
                        )}
                        {task.isOverdue && !task.is_completed && (
                          <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">
                            Overdue
                          </span>
                        )}
                      </div>
                      
                      <div className="text-sm text-muted-foreground">
                        {task.due_date && (
                          <p>Due: {format(parseISO(task.due_date), "MMM d, yyyy")}</p>
                        )}
                        {task.due_at_mileage && (
                          <p>Due at: {task.due_at_mileage.toLocaleString()} miles</p>
                        )}
                        {task.isEstimated && (
                          <p className="text-xs italic">
                            Est. date: {format(task.estimatedDate, "MMM d, yyyy")}
                          </p>
                        )}
                      </div>

                      {task.description && (
                        <p className="text-sm border-t pt-2">{task.description}</p>
                      )}

                      {task.attachments && task.attachments.length > 0 && (
                        <div className="border-t pt-2">
                          <p className="text-xs font-medium mb-1">Attachments:</p>
                          <div className="flex flex-wrap gap-1">
                            {task.attachments.map((url, idx) => (
                              <a
                                key={idx}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs text-primary hover:underline"
                              >
                                <FileText className="h-3 w-3" />
                                {url.split('/').pop()?.slice(0, 15)}...
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between border-t pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => onToggleComplete(task.id, !task.is_completed)}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          {task.is_completed ? "Mark Incomplete" : "Mark Complete"}
                        </Button>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => openEditDialog(task)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => openDeleteDialog(task)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              ))}
            </div>

            {/* Timeline labels */}
            <div className="relative mt-1 h-8">
              {/* Start date */}
              <span className="absolute left-0 text-[10px] text-muted-foreground">
                {format(timelineData.startDate, "MMM d")}
              </span>
              
              {/* Monthly markers */}
              {timelineData.monthMarkers.map((marker, idx) => (
                <span
                  key={idx}
                  className="absolute text-[10px] text-muted-foreground -translate-x-1/2"
                  style={{ left: `${marker.position}%` }}
                >
                  {marker.label}
                </span>
              ))}
              
              {/* Today label */}
              <div 
                className="absolute text-[10px] font-medium text-primary -translate-x-1/2 text-center mt-3"
                style={{ left: `${timelineData.todayPosition}%` }}
              >
                Today
              </div>
              
              {/* End date */}
              <span className="absolute right-0 text-[10px] text-muted-foreground">
                {format(timelineData.endDate, "MMM d, yyyy")}
              </span>
            </div>
          </div>
        )}

        {/* Add Task Dialog */}
        <MaintenanceTaskDialog
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          onSubmit={handleAddSubmit}
          vehicle={vehicle}
          isLoading={isAddingTask}
          uploadAttachment={uploadAttachment}
          mode="add"
        />

        {/* Edit Task Dialog */}
        <MaintenanceTaskDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSubmit={handleEditSubmit}
          vehicle={vehicle}
          isLoading={isEditingTask}
          uploadAttachment={uploadAttachment}
          task={selectedTask}
          mode="edit"
        />

        {/* Delete Confirmation Dialog */}
        <DeleteTaskDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          task={selectedTask}
          onConfirm={handleDeleteConfirm}
          isLoading={isDeletingTask}
        />
      </CardContent>
    </Card>
  );
};
